/**
 * Data layer for the COMPANY panel's Marketplace insights section, backed by
 * the company/v1 marketplace-hub endpoints (token-scoped to the company; the
 * B2B/Members toggle is appended by api/companyApi.js on every call).
 *
 * Page-facing shapes come from the shared adapters in
 * container/MarketplaceHub/marketplaceHubApi.js, so a future super-admin
 * version of these dashboards reads the payload identically.
 *
 * Every fetcher takes an optional `department` so the section's filter can
 * narrow the whole page to one department.
 */

import {
  getCompanyEGiftsDashboard,
  getCompanyVipOffersDashboard,
  getCompanyMarketplaceUsers,
  getCompanyMarketplaceUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';
import {
  adaptEGifts,
  adaptVipOffers,
  adaptUserDetail
} from '../../../MarketplaceHub/marketplaceHubApi';

export const fetchCompanyEGifts = async (department = '') => {
  try {
    const data = unwrap(await getCompanyEGiftsDashboard(department));
    return {
      ...adaptEGifts(data),
      // Company-only: the size of the population, so the page can say
      // "18 of 240 employees have redeemed".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyEGifts error:', error);
    return null;
  }
};

export const fetchCompanyVipOffers = async (department = '') => {
  try {
    const data = unwrap(await getCompanyVipOffersDashboard(department));
    return {
      ...adaptVipOffers(data),
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyVipOffers error:', error);
    return null;
  }
};

// Matches the MarketplaceUsersTable fetcher contract.
export const fetchCompanyMarketplaceUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyMarketplaceUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyMarketplaceUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's marketplace profile. The backend 404s any user
 * outside this company, so no scoping is needed here.
 */
export const fetchCompanyMarketplaceUserDetail = async (userId) => {
  try {
    return adaptUserDetail(unwrap(await getCompanyMarketplaceUserDetail(userId)));
  } catch (error) {
    console.error('fetchCompanyMarketplaceUserDetail error:', error);
    return null;
  }
};
